import {useState, useEffect} from 'react';
import DOMPurify from 'dompurify';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Modal from '@mui/material/Modal';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { IoClose as Close } from 'react-icons/io5';
import { IoMailOutline as Email } from 'react-icons/io5';
import { HiOutlineCog6Tooth as AccessCode } from '@Icons/hi2/HiOutlineCog6Tooth';
import { validate, res } from 'react-email-validator';
import OTPInput, { ResendOTP } from 'otp-input-react';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import { useReduxUsers } from '@Hooks/reduxUsersHook/useReduxUsers';
import BorderBlueButtonV2 from '@CommonRepresentationalDisplay/Buttons/BorderBlueButtonV2';
import BorderWhiteButtonV2 from '@CommonRepresentationalDisplay/Buttons/BorderWhiteButtonV2';
import SignupService from '@Helpers/URLHelpers/SignupService';
import UserInfoService from '@Helpers/URLHelpers/UserInfoService';
import EmailVerificationService from '@Helpers/URLHelpers/AuthService/EmailVerificationService';
import ErrorHandler from '@Root/ErrorHandler';
import './VerifyUpdateEmailForm.scss';

export default function VerifyUpdateEmailForm(props) {

    const [cursor, setCursor] = useState('auto');
    const [user, setUser] = useState(null);
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState(0)
    const [emailLoader, setEmailLoader] = useState(false);
    const [emailTaken, setEmailTaken] = useState(false);
    const [stateSeconds, setStateSeconds] = useState(0);
    const [invalidEmail, setInvalidEmail] = useState(false);
    const [isVerifyTimer, setIsVerifyTimer] = useState(false);
    const [refresh, setRefresh] = useState(false);
    const [verifLoader, setVerifyLoader] = useState(false);
    const [disableClick, setDisableClick] = useState(false);
    const [isVerifiedEmail, setIsVerifiedEmail] = useState(false);
    const [emailVerificationCodeGenerated, setEmailVerificationCodeGenerated] = useState(false);
    const [emailVerificationCode, setEmailVerificationCode] = useState(null);
    const [isValidVerificationCode, setIsValidVerificationCode] = useState(false);
    const [disableVerifyButton, setDisableVerifyButton] = useState(false);
    const [invalidVerificationCode, setInvalidVerificationCode] = useState(false);
    const [snackBarOpen, setSnackBarOpen] = useState(false);
    const [snackBarType, setSnackBarType] = useState('success');
    const [snackBarMessage, setSnackBarMessage] = useState('');
    const { setUpdateUser } = useReduxUsers();
    const { setActiveUser } = useReduxActiveUser();
    let seconds = 0;
    let timer = 0;
    const signupService = new SignupService();
    const userInfoService = new UserInfoService();
    const errorHandler = new ErrorHandler();
    const emailVerificationService = new EmailVerificationService();
    const className = 'verifyUpdateEmailForm';

    useEffect(()=>{
        renderData();
    },[])

    const renderData = ()=> {
        setUser(props.user);
        setEmail(props.user?.email);
    }

    const emailChangeHandler = async e => {
        setEmailLoader(true);
        setEmailTaken(null);
        setEmailVerificationCodeGenerated(false);
        let sanitizedEmail = DOMPurify.sanitize(e.target.value);
        setEmail(sanitizedEmail);
        let emailValid = emailValidCheck(sanitizedEmail);
        setInvalidEmail(!emailValid);
        if(emailValid){
            let emailTakenVar = await emailTakenCheck(sanitizedEmail);
            setEmailTaken(emailTakenVar);
        }
        setEmailLoader(false);
    }

    const emailVerificationCodeChangeHandler = async e =>{
        let sanitizedVerificationCode = DOMPurify.sanitize(e.target.value);
        setEmailVerificationCode(sanitizedVerificationCode);
        if(sanitizedVerificationCode.length === 6){
            setIsValidVerificationCode(true);
        }
    }

    const emailTakenCheck = async(email) => {
        let user = await signupService.getUserByEmailForSignup(email);
        if(!!user && !!user?.body && !!JSON.parse(user?.body) && Object.keys(JSON.parse(user?.body)).length !== 0 && JSON.parse(user?.body)?.id > 0)
            return true;
        return false;
    }

    function emailValidCheck(email){
        validate(email);
        if(res){
            return true;
        } else {
            return false;
        }
    }

    const handleVerifyEmail = async e => {
        setDisableVerifyButton(true);
        seconds = 30;
        setStateSeconds(30);
        setIsVerifyTimer(true);
        startTimer();
        let emailVerification = await emailVerificationService.generateEmailVerificationCode(email);
        if(emailVerification.body === 'true'){
            setEmailVerificationCodeGenerated(true);
        } else {
            setEmailVerificationCodeGenerated(false);
        }
    }

    const startTimer = () => {
        if (timer === 0) {
            timer = setInterval(countDown, 1000);
        }
    }

    const countDown = () => {
        if(seconds > 0) {
            seconds = seconds - 1;
            setStateSeconds(seconds);
            setRefresh(!refresh);
        } else {
            clearInterval(timer);
            timer = 0;
            setRefresh(!refresh);
            setIsVerifyTimer(false);
            setDisableVerifyButton(false);
        }
    }

    const handleVerifyOTP = async e => {
        setDisableClick(true);
        setCursor('wait');
        setVerifyLoader(true);
        let otpBody = {
            'toEmail': email,
            'verificationCode': otp
        }
        let codeVerification = await emailVerificationService.verifyEmailVerificationCode(otpBody);
        codeVerification = JSON.parse(codeVerification.body);
        if(codeVerification === true){
            setInvalidVerificationCode(false);
            setIsVerifiedEmail(true);
        } else {
            setInvalidVerificationCode(true);
            setIsVerifiedEmail(false);
            setSnackBarMessage('Incorrect OTP, Please try again!');
            setSnackBarType('error');
            setSnackBarOpen(true);
        }
        setCursor('auto');
        setDisableClick(false);
        setVerifyLoader(false);
    }

    const handleUpdateEmail = async e => {
        e?.preventDefault();
        setEmailLoader(true);
        try {
            let userObj = user;
            userObj.email = email;
            let updatedUser = await userInfoService.updateUserInfo(userObj);
            if(updatedUser.body == undefined) {
                setSnackBarMessage('Failed to update. Please try again!');
                setSnackBarType('error');
                setSnackBarOpen(true);
                return;
            }
            setUpdateUser(userObj.id, updatedUser);
            updatedUser = JSON.parse(updatedUser.body);
            setUser(updatedUser);
            setActiveUser(updatedUser);
            setSnackBarMessage('Email Changed!');
            setSnackBarType('success');
            setSnackBarOpen(true);
            await props.reloadData();
            props.closePopup();
        } catch (err) {
            errorHandler.handleError(err);
        }
        setEmailLoader(false);
    }

    const handleSnackBarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackBarOpen(false);
    };

    const renderButton = (buttonProps) => {
        return <button {...buttonProps} className={className + '__inner__body__form__span__resend grey-text is-clickable'}>
            Resend
        </button>;
    };

    const renderTime = (remainingTime) => {
        return <div className={className + '__inner__body__form__span ' + (remainingTime === 0 ? 'standard-text-normal' : 'alert-text')}>
            Please wait {remainingTime} seconds to try again
        </div>;
    };

    return (
        <Modal
            open={props.open}
            onClose={props.closePopup}
            style={{ cursor: cursor }}>
                <div className={className + ' width-wfa'}>
                    <div className={className + '__inner'}>
                        <div className={className + '__inner__titleBar flex-row-center-space-between'}>
                            <div className={className + '__inner__titleBar__title flex-row-center-1vw-gap'}>
                                <Email className={className + '__inner__titleBar__title__indicator blue-indicator' }/>
                                <div className={className + '__inner__titleBar__title__t1 standard-text-semi-large'}>Update Email</div>
                            </div>
                            <Close className={className + '__inner__titleBar__close close-button'}
                                onClick={props.closePopup}/>
                        </div>
                        <div className={className + '__inner__body flex-column-flex-start-2vh-gap'}>
                            <form className={className + '__inner__body__form width-wfa'}>
                                <div className={className + '__inner__body__form__top'}>
                                    <label className={className + '__inner__body__form__email width-wfa'}>
                                        <TextField type='text' className={className + '__inner__body__form__email__input standard-textField width-wfa'}
                                            placeholder='Email' onChange={emailChangeHandler}
                                            value={email}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position='start'>
                                                        <Email className={className + '__inner__body__form__email__emailIcon grey-text'} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </label>
                                    {
                                        !emailVerificationCodeGenerated &&
                                            <div className={className + '__inner__body__form__sendOtp flex-row-center-space-between'}>
                                                <div className={className + '__inner__body__form__sendOtp__button'}>
                                                    <BorderBlueButtonV2
                                                        onClick={handleVerifyEmail}
                                                        disabled={!email || invalidEmail || emailTaken || disableVerifyButton || cursor === 'wait'}
                                                        text='Send OTP'/>
                                                </div>
                                                {
                                                    (!invalidEmail && email != null && !emailLoader && emailTaken) &&
                                                    <span className={className + '__inner__body__form__span alert-text small-text'}>Email already registered</span>
                                                }
                                                {
                                                    (!invalidEmail && email != null && !emailLoader && !emailTaken && !disableVerifyButton) &&
                                                    <span className={className + '__inner__body__form__span success-text small-text'}>Email available</span>
                                                }
                                                {
                                                    invalidEmail &&
                                                    <span className={className + '__inner__body__form__span alert-text small-text'}>Invalid Email</span>
                                                }
                                            </div>
                                    }
                                    <div className={className + '__inner__body__form__otpMsg'}>
                                        {
                                            (emailVerificationCodeGenerated && !isValidVerificationCode) &&
                                            <span className={className + '__inner__body__form__span standard-text-normal small-text'}>The OTP sent is valid for 15 minutes, enter a 6 digit code sent to your email</span>
                                        }
                                    </div>
                                    {
                                        emailVerificationCodeGenerated &&
                                            <>
                                                <div className={className + '__inner__body__bottom__otp__input flex-column-center-1vh-gap'}>
                                                    <OTPInput value={otp} onChange={setOtp} autoFocus OTPLength={6} otpType='number' disabled={!emailVerificationCodeGenerated || isVerifiedEmail} />
                                                    <ResendOTP maxTime={stateSeconds} onResendClick={handleVerifyEmail} renderButton={renderButton} renderTime={renderTime} />
                                                </div>
                                                <div className={className + '__inner__body__form__verify'}>
                                                    <div className={className + '__inner__body__form__verify__button'}>
                                                        <BorderBlueButtonV2
                                                            onClick={handleVerifyOTP}
                                                            disabled={(otp.length !== 6) || isVerifiedEmail || cursor === 'wait'}
                                                            text='Verify OTP'/>
                                                    </div>
                                                </div>
                                            </>
                                    }
                                </div>
                                <div className={className + '__inner__body__form__bottom flex-row-flex-end'}>
                                    <div className={className + '__inner__body__form__bottom__buttons flex-row-center-1vw-gap'}>
                                        <BorderBlueButtonV2
                                            onClick={handleUpdateEmail}
                                            disabled={!isVerifiedEmail || (otp?.length !== 6) || cursor === 'wait'}
                                            text=
                                                <div className={'flex-column-center'}>
                                                    <div className={'flex-row-center-1vw-gap'}>
                                                        {
                                                            emailLoader &&
                                                            <CircularProgress className='white-text' size='1rem' thickness={5} />
                                                        }
                                                        Update Email
                                                    </div>
                                                </div>
                                            />
                                        <BorderWhiteButtonV2
                                            onClick={props.closePopup}
                                            text='Back'/>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                    <Snackbar
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center'
                        }}
                        open={snackBarOpen}
                        autoHideDuration={5000}
                        onClose={handleSnackBarClose}>
                        <Alert
                            onClose={handleSnackBarClose}
                            severity={snackBarType}
                            variant='filled'
                            sx={{ width: '100%' }}>
                            {snackBarMessage}
                        </Alert>
                    </Snackbar>
                </div>
        </Modal>
    )
}